import React, { useMemo, useState } from "react";
import { db } from "./firebase";
import { addDoc, collection, serverTimestamp } from "firebase/firestore";
import bg from "./mcd.png";

export default function RequestPage() {
  // ==============================
  // PIN LOGIN
  // ==============================
  const PIN_MAP = {
    "0627": "Estadio",
    "1010": "Torrecardenas",
    "1351": "Rambla",
  };

  const [pin, setPin] = useState("");
  const [restaurant, setRestaurant] = useState("");
  const [pinOk, setPinOk] = useState(false);

  const handlePinSubmit = (e) => {
    e.preventDefault();
    const r = PIN_MAP[pin.trim()];
    if (!r) {
      alert("PIN no válido. Revisa el número de Site de tu restaurante.");
      setPin("");
      setRestaurant("");
      setPinOk(false);
      return;
    }
    setRestaurant(r);
    setPinOk(true);
  };

  // ==============================
  // OPCIONES Y RESTRICCIONES
  // ==============================
  const TYPES = [
    "Pantalón",
    "Camiseta",
    "Chaqueta",
    "Americana",       // solo PEC
    "Chaleco",         // solo Encargado
    "Calzas",
    "Gorra",
    "Bandana",
    "Pañuelo",
    "Cinturón",
    "Delantal largo",
    "Delantal corto",
  ];

  const ROLES_BASE = ["Crew", "PEC", "Encargado", "Todos"];
  const SEXES_BASE = ["Hombre", "Mujer"];

  const SHIRT_SIZES = [
    "2XS","XS","S","M","L","XL","2XL","3XL","4XL","5XL",
  ];
  const PANT_SIZES = Array.from({ length: 21 }, (_, i) => 32 + i * 2); // 32..72 pares
  const BELT_SIZES = ["L", "XL", "2XL"];
  const CALZAS_SIZES = ["S", "M", "L", "XL"];
  const UNIQUE_SIZE = ["Única"];

  const LOCK_ALL = new Set([
    "Gorra",
    "Bandana",
    "Pañuelo",
    "Delantal largo",
    "Delantal corto",
  ]);

  const isBelt = (t) => t === "Cinturón";
  const isCalzas = (t) => t === "Calzas";
  const isChaqueta = (t) => t === "Chaqueta";
  const isShirtFamily = (t) =>
    t === "Camiseta" || t === "Chaqueta" || t === "Americana" || t === "Chaleco";
  const locksSex = (t) => LOCK_ALL.has(t) || isBelt(t) || isCalzas(t);
  const locksSize = (t) => LOCK_ALL.has(t);
  const locksRole = (t) =>
    LOCK_ALL.has(t) || isBelt(t) || t === "Chaqueta" || t === "Pañuelo" ||
    t === "Delantal largo" || t === "Delantal corto" || t === "Gorra" || t === "Bandana"||
  t === "Chaqueta" || t === "Americana" || t === "Chaleco"|| t === "Calzas";

  const roleOptionsForType = (t) => {
    if (t === "Americana") return ["PEC"];
    if (t === "Chaleco") return ["Encargado"];
    if (LOCK_ALL.has(t) || isBelt(t) || isCalzas(t)|| isChaqueta(t)) return ["Todos"];
    
    return ROLES_BASE.filter((r) => r !== "Todos"); // prendas normales no deberían pedir "Todos"
  };

  const sexOptionsForType = (t) => {
    if (locksSex(t)) return ["Unisex"];
    return SEXES_BASE;
  };

  const sizeOptionsForType = (t) => {
    if (LOCK_ALL.has(t)) return UNIQUE_SIZE;
    if (isBelt(t)) return BELT_SIZES;
    if (isCalzas(t)) return CALZAS_SIZES;
    if (t === "Pantalón") return PANT_SIZES.map(String);
    if (isShirtFamily(t)) return SHIRT_SIZES;
    return [];
  };

  // ==============================
  // LINEAS DE PETICIÓN
  // ==============================
  const emptyLine = {
    tipo: "",
    sexo: "",
    rol: "",
    talla: "",
    cantidad: 1,
    entrenador: false,
    premama: false,
  };

  const [employeeName, setEmployeeName] = useState("");
  const [lines, setLines] = useState([emptyLine]);
  const [sending, setSending] = useState(false);

  const applyLocks = (line) => {
    const t = line.tipo;
    const lockedRol = locksRole(t);
    const lockedSexo = locksSex(t);
    const lockedTalla = locksSize(t);

    const roles = roleOptionsForType(t);
    const sexs = sexOptionsForType(t);
    const sizes = sizeOptionsForType(t);

    let next = { ...line };

    if (lockedRol || !roles.includes(next.rol)) next.rol = roles[0] || "";
    if (lockedSexo || !sexs.includes(next.sexo)) next.sexo = sexs[0] || "";
    if (lockedTalla || !sizes.includes(next.talla)) next.talla = sizes[0] || "";

    // Entrenador solo para Camiseta + Crew
    const entrenadorValido = next.tipo === "Camiseta" && next.rol === "Crew";
    if (!entrenadorValido) next.entrenador = false;
    const premamaValida = next.tipo === "Pantalón" && next.sexo === "Mujer";
    if (!premamaValida) next.premama = false;

    // ✅ asegurar cantidades válidas 1–2 aunque venga algo raro
    const c = Number(next.cantidad) || 1;
    next.cantidad = c < 1 ? 1 : c > 2 ? 2 : c;

    return next;
  };

  const updateLine = (idx, patch) => {
    setLines((prev) => {
      const copy = [...prev];
      const merged = { ...copy[idx], ...patch };
      copy[idx] = applyLocks(merged);
      return copy;
    });
  };

  const addLine = () => setLines((p) => [...p, emptyLine]);

  const removeLine = (idx) =>
    setLines((p) => (p.length === 1 ? [emptyLine] : p.filter((_, i) => i !== idx)));

  const canSend =
    pinOk &&
    employeeName.trim() &&
    lines.every((l) => {
      const c = Number(l.cantidad);
      return l.tipo && l.sexo && l.rol && l.talla && (c === 1 || c === 2);
    });

  const sendRequests = async () => {
    if (!canSend) {
      alert("Completa PIN, empleado y todas las líneas antes de enviar.");
      return;
    }
    setSending(true);
    try {
      for (const l of lines) {
        await addDoc(collection(db, "requests"), {
          restaurante: restaurant,
          empleado: employeeName.trim(),
          tipo: l.tipo,
          sexo: l.sexo,
          rol: l.rol,
          talla: l.talla,
          cantidad: Number(l.cantidad),
          entrenador: !!l.entrenador,
          premama: !!l.premama,
          createdAt: serverTimestamp(),
        });
      }
      alert("Peticiones enviadas correctamente. ¡Gracias!");
      setLines([emptyLine]);
      setEmployeeName("");
    } catch (e) {
      console.error(e);
      alert("Error al enviar peticiones.");
    } finally {
      setSending(false);
    }
  };

  // ==============================
  // UI
  // ==============================
  return (
    <div
      className="min-h-screen bg-cover bg-center"
      style={{ backgroundImage: `url(${bg})` }}
    >
      <div className="min-h-screen bg-white/70">
        <div className="max-w-5xl mx-auto px-4 py-8">
          <div className="bg-white/90 rounded-3xl shadow-xl p-8">
            <h1 className="text-3xl font-extrabold text-emerald-900 text-center">
              Petición de uniformes
            </h1>
            <p className="text-center text-slate-600 mt-2">
              El Pin (4 dígitos) es el número de Site de tu restaurante
            </p>

            {/* PIN FORM (solo si NO está validado) */}
            {!pinOk && (
              <form onSubmit={handlePinSubmit} className="mt-8">
                <label className="block text-sm font-semibold text-slate-800 mb-2">
                  PIN restaurante
                </label>
                <div className="flex gap-3">
                  <input
                    type="password"
                    inputMode="numeric"
                    maxLength={4}
                    value={pin}
                    onChange={(e) => setPin(e.target.value)}
                    className="flex-1 rounded-xl border border-slate-200 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-amber-400"
                    placeholder="••••"
                  />
                  <button
                    type="submit"
                    className="px-6 py-3 rounded-xl font-semibold text-white bg-gradient-to-r from-amber-400 to-emerald-800 shadow-md hover:opacity-90"
                  >
                    Entrar
                  </button>
                </div>
              </form>
            )}

            {/* FORMULARIO (solo si PIN OK) */}
            {pinOk && (
              <>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-8">
                  <div>
                    <label className="block text-sm font-semibold text-slate-800 mb-2">
                      Restaurante
                    </label>
                    <input
                      value={restaurant}
                      readOnly
                      className="w-full rounded-xl border border-slate-200 px-4 py-3 bg-slate-50 text-slate-700"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-semibold text-slate-800 mb-2">
                      Empleado
                    </label>
                    <input
                      value={employeeName}
                      onChange={(e) => setEmployeeName(e.target.value)}
                      className="w-full rounded-xl border border-slate-200 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-amber-400"
                      placeholder="Nombre y apellidos"
                    />
                  </div>
                </div>

                {/* ===== DESKTOP: tabla ===== */}
                <div className="mt-6 hidden md:block">
                  <table className="w-full text-sm table-fixed">
                    <colgroup>
                      <col style={{ width: "17%" }} />  {/* Tipo */}
                      <col style={{ width: "14%" }} />  {/* Sexo */}
                      <col style={{ width: "15%" }} />  {/* Rol */}
                      <col style={{ width: "12%" }} />  {/* Talla */}
                      <col style={{ width: "8%" }} />   {/* Cantidad */}
                      <col style={{ width: "10%" }} />  {/* Entrenador */}
                      <col style={{ width: "10%" }} />  {/* Premamá */}
                      <col style={{ width: "10%" }} />  {/* Acciones */}
                    </colgroup>

                    <thead className="bg-amber-50 text-slate-900">
                      <tr>
                        <th className="text-left p-3">Tipo</th>
                        <th className="text-left p-3">Sexo</th>
                        <th className="text-left p-3">Rol</th>
                        <th className="text-left p-3">Talla</th>
                        <th className="text-left p-3">Cantidad</th>
                        <th className="text-center p-3">Entrenador</th>
                        <th className="text-center p-3">Premamá</th>
                        <th className="text-right p-3">Acciones</th>
                      </tr>
                    </thead>

                    <tbody>
                      {lines.map((l, idx) => {
                        const roles = roleOptionsForType(l.tipo);
                        const sexs = sexOptionsForType(l.tipo);
                        const sizes = sizeOptionsForType(l.tipo);

                        const entrenadorValido =
                          l.tipo === "Camiseta" && l.rol === "Crew";

                        const premamaValido =
                          l.tipo === "Pantalón" && l.sexo === "Mujer";

                        return (
                          <tr key={idx} className="border-b">
                            {/* TIPO */}
                            <td className="p-3">
                              <select
                                value={l.tipo}
                                onChange={(e) =>
                                  updateLine(idx, { tipo: e.target.value })
                                }
                                className="w-full rounded-lg border border-slate-200 px-3 py-2"
                              >
                                <option value="">Tipo...</option>
                                {TYPES.map((t) => (
                                  <option key={t} value={t}>
                                    {t}
                                  </option>
                                ))}
                              </select>
                            </td>

                            {/* SEXO */}
                            <td className="p-3">
                              <select
                                value={l.sexo}
                                disabled={locksSex(l.tipo)}
                                onChange={(e) =>
                                  updateLine(idx, { sexo: e.target.value })
                                }
                                className="w-full rounded-lg border border-slate-200 px-3 py-2 disabled:bg-slate-100"
                              >
                                <option value="">Sexo...</option>
                                {sexs.map((s) => (
                                  <option key={s} value={s}>
                                    {s}
                                  </option>
                                ))}
                              </select>
                            </td>

                            {/* ROL */}
                            <td className="p-3">
                              <select
                                value={l.rol}
                                disabled={locksRole(l.tipo)}
                                onChange={(e) =>
                                  updateLine(idx, { rol: e.target.value })
                                }
                                className="w-full rounded-lg border border-slate-200 px-3 py-2 disabled:bg-slate-100"
                              >
                                <option value="">Rol...</option>
                                {roles.map((r) => (
                                  <option key={r} value={r}>
                                    {r}
                                  </option>
                                ))}
                              </select>
                            </td>

                            {/* TALLA */}
                            <td className="p-3">
                              <select
                                value={l.talla}
                                disabled={locksSize(l.tipo)}
                                onChange={(e) =>
                                  updateLine(idx, { talla: e.target.value })
                                }
                                className="w-full rounded-lg border border-slate-200 px-3 py-2 disabled:bg-slate-100"
                              >
                                <option value="">Talla</option>
                                {sizes.map((z) => (
                                  <option key={z} value={z}>
                                    {z}
                                  </option>
                                ))}
                              </select>
                            </td>

                            {/* CANTIDAD (DESPLEGABLE 1–2) */}
                            <td className="p-3">
                              <select
                                value={l.cantidad}
                                onChange={(e) =>
                                  updateLine(idx, {
                                    cantidad: Number(e.target.value),
                                  })
                                }
                                className="w-full rounded-lg border border-slate-200 px-3 py-2 bg-white"
                              >
                                <option value={1}>1</option>
                                <option value={2}>2</option>
                              </select>
                            </td>

                            {/* ENTRENADOR */}
                            <td className="p-3 text-center">
                              {entrenadorValido ? (
                                <input
                                  type="checkbox"
                                  checked={l.entrenador}
                                  onChange={(e) =>
                                    updateLine(idx, {
                                      entrenador: e.target.checked,
                                    })
                                  }
                                />
                              ) : (
                                <span className="text-slate-400">-</span>
                              )}
                            </td>

                            {/* PREMAMÁ */}
                            <td className="p-3 text-center">
                              {premamaValido ? (
                                <input
                                  type="checkbox"
                                  checked={l.premama}
                                  onChange={(e) =>
                                    updateLine(idx, {
                                      premama: e.target.checked,
                                    })
                                  }
                                />
                              ) : (
                                <span className="text-slate-400">-</span>
                              )}
                            </td>

                            {/* ACCIONES */}
                            <td className="p-3 text-right">
                              <button
                                type="button"
                                onClick={() => removeLine(idx)}
                                className="px-3 py-2 rounded-lg border hover:bg-slate-50"
                              >
                                Eliminar
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* ===== MÓVIL: tarjetas ===== */}
                <div className="mt-6 grid grid-cols-1 gap-4 md:hidden">
                  {lines.map((l, idx) => {
                    const roles = roleOptionsForType(l.tipo);
                    const sexs = sexOptionsForType(l.tipo);
                    const sizes = sizeOptionsForType(l.tipo);

                    const entrenadorValido =
                      l.tipo === "Camiseta" && l.rol === "Crew";

                    const premamaValido =
                      l.tipo === "Pantalón" && l.sexo === "Mujer";

                    return (
                      <div
                        key={idx}
                        className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm"
                      >
                        <div className="grid grid-cols-1 gap-3">
                          {/* Tipo */}
                          <div>
                            <label className="block text-xs font-semibold text-slate-600 mb-1">
                              Tipo
                            </label>
                            <select
                              value={l.tipo}
                              onChange={(e) =>
                                updateLine(idx, { tipo: e.target.value })
                              }
                              className="w-full rounded-lg border border-slate-200 px-3 py-2"
                            >
                              <option value="">Tipo...</option>
                              {TYPES.map((t) => (
                                <option key={t} value={t}>
                                  {t}
                                </option>
                              ))}
                            </select>
                          </div>

                          {/* Sexo */}
                          <div>
                            <label className="block text-xs font-semibold text-slate-600 mb-1">
                              Sexo
                            </label>
                            <select
                              value={l.sexo}
                              disabled={locksSex(l.tipo)}
                              onChange={(e) =>
                                updateLine(idx, { sexo: e.target.value })
                              }
                              className="w-full rounded-lg border border-slate-200 px-3 py-2 disabled:bg-slate-100"
                            >
                              <option value="">Sexo...</option>
                              {sexs.map((s) => (
                                <option key={s} value={s}>
                                  {s}
                                </option>
                              ))}
                            </select>
                          </div>

                          {/* Rol */}
                          <div>
                            <label className="block text-xs font-semibold text-slate-600 mb-1">
                              Rol
                            </label>
                            <select
                              value={l.rol}
                              disabled={locksRole(l.tipo)}
                              onChange={(e) =>
                                updateLine(idx, { rol: e.target.value })
                              }
                              className="w-full rounded-lg border border-slate-200 px-3 py-2 disabled:bg-slate-100"
                            >
                              <option value="">Rol...</option>
                              {roles.map((r) => (
                                <option key={r} value={r}>
                                  {r}
                                </option>
                              ))}
                            </select>
                          </div>

                          {/* Talla */}
                          <div>
                            <label className="block text-xs font-semibold text-slate-600 mb-1">
                              Talla
                            </label>
                            <select
                              value={l.talla}
                              disabled={locksSize(l.tipo)}
                              onChange={(e) =>
                                updateLine(idx, { talla: e.target.value })
                              }
                              className="w-full rounded-lg border border-slate-200 px-3 py-2 disabled:bg-slate-100"
                            >
                              <option value="">Talla...</option>
                              {sizes.map((z) => (
                                <option key={z} value={z}>
                                  {z}
                                </option>
                              ))}
                            </select>
                          </div>

                          {/* Cantidad (DESPLEGABLE 1–2) */}
                          <div>
                            <label className="block text-xs font-semibold text-slate-600 mb-1">
                              Cantidad
                            </label>
                            <select
                              value={l.cantidad}
                              onChange={(e) =>
                                updateLine(idx, {
                                  cantidad: Number(e.target.value),
                                })
                              }
                              className="w-full rounded-lg border border-slate-200 px-3 py-2 bg-white"
                            >
                              <option value={1}>1</option>
                              <option value={2}>2</option>
                            </select>
                          </div>

                          {/* Entrenador */}
                          <div className="flex items-center justify-between">
                            <span className="text-xs font-semibold text-slate-600">
                              Entrenador
                            </span>
                            {entrenadorValido ? (
                              <input
                                type="checkbox"
                                checked={l.entrenador}
                                onChange={(e) =>
                                  updateLine(idx, {
                                    entrenador: e.target.checked,
                                  })
                                }
                              />
                            ) : (
                              <span className="text-slate-400 text-sm">-</span>
                            )}
                          </div>

                          {/* Premamá */}
                          <div className="flex items-center justify-between">
                            <span className="text-xs font-semibold text-slate-600">
                              Premamá
                            </span>
                            {premamaValido ? (
                              <input
                                type="checkbox"
                                checked={l.premama}
                                onChange={(e) =>
                                  updateLine(idx, { premama: e.target.checked })
                                }
                              />
                            ) : (
                              <span className="text-slate-400 text-sm">-</span>
                            )}
                          </div>

                          {/* Eliminar */}
                          <button
                            type="button"
                            onClick={() => removeLine(idx)}
                            className="mt-2 w-full px-3 py-2 rounded-xl border hover:bg-slate-50"
                          >
                            Eliminar prenda
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div className="mt-6 flex items-center justify-between">
                  <button
                    type="button"
                    onClick={addLine}
                    className="px-4 py-2 rounded-xl border border-slate-200 hover:bg-slate-50"
                  >
                    + Añadir prenda
                  </button>

                  <button
                    type="button"
                    disabled={!canSend || sending}
                    onClick={sendRequests}
                    className="px-6 py-3 rounded-xl font-semibold text-white bg-gradient-to-r from-amber-400 to-emerald-800 shadow-md disabled:opacity-50"
                  >
                    {sending ? "Enviando..." : "Enviar peticiones"}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
